import React from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Mail, Bell, Shield, Database, Globe, Key, Users, Settings,
  ChevronRight, Server, FileText, AlertCircle, Clock 
} from 'lucide-react';
import './Settings.css';

// The component now only needs a single 'onNavigate' prop for all navigation actions.
const AdminSettings = ({ onNavigate }) => {

  // This is a "safe" handler. It checks if the onNavigate function was actually passed
  // as a prop before trying to call it. This prevents the "is not a function" error.

  const handleCardClick = (path) => {
    if (onNavigate && typeof onNavigate === 'function') {
      onNavigate(path);
    } else {
      // Log a warning in the console if the prop is missing, which helps with debugging.
      console.error(`Navigation to "${path}" was attempted, but the 'onNavigate' prop is missing or is not a function.`);
    }
  };

  const settingsCategories = [
    {
      title: 'Communication',
      description: 'Manage email and notification settings',
      items: [
        {
          icon: Mail,
          title: 'Email Service',
          description: 'Configure email templates and SMTP settings',
          // Standardized to use the safe navigation handler.
          action: () => handleCardClick('emailconfiguration'),
          color: '#4f46e5'
        },
        // {
        //   icon: Bell,
        //   title: 'Notification Service',
        //   description: 'Manage system notifications and alerts',
        //   action: () => alert('Notification Service coming soon!'),
        //   color: '#f59e0b',
        //   comingSoon: true
        // }
      ]
    },
    // {
    //   title: 'Security & Access',
    //   description: 'Control access and security settings',
    //   items: [
    //     {
    //       icon: Shield,
    //       title: 'Security Settings',
    //       description: 'Password policies and two-factor authentication',
    //       action: () => alert('Security Settings coming soon!'),
    //       color: '#10b981',
    //       comingSoon: true
    //     },
    //     {
    //       icon: Users,
    //       title: 'User Management',
    //       description: 'Manage admin users and permissions',
    //       action: () => alert('User Management coming soon!'),
    //       color: '#3b82f6',
    //       comingSoon: true
    //     },
    //     {
    //       icon: Key,
    //       title: 'API Keys',
    //       description: 'Manage API keys and integrations',
    //       action: () => alert('API Keys coming soon!'),
    //       color: '#8b5cf6',
    //       comingSoon: true
    //     }
    //   ]
    // },
    {
      title: 'System Configuration',
      description: 'Configure system-wide settings',
      items: [
        {
           icon: Clock,
          title: 'Job Scheduler',
          description: 'Manage and monitor automated background tasks',
          // Standardize to use the safe navigation handler
          action: () => handleCardClick('job-scheduler'), 
          color: '#a855f7',
        },
        // {
        //   icon: Database,
        //   title: 'Database Settings',
        //   description: 'Database connection and backup settings',
        //   action: () => alert('Database Settings coming soon!'),
        //   color: '#6366f1',
        //   comingSoon: true
        // },
        // {
        //   icon: Globe,
        //   title: 'Regional Settings',
        //   description: 'Language, timezone, and currency settings',
        //   action: () => alert('Regional Settings coming soon!'),
        //   color: '#14a5a0',
        //   comingSoon: true
        // },
        // {
        //   icon: Server,
        //   title: 'Server Configuration',
        //   description: 'Server settings and performance tuning',
        //   action: () => alert('Server Configuration coming soon!'),
        //   color: '#ec4899',
        //   comingSoon: true
        // }
      ]
    },
    // {
    //   title: 'Reports & Logs',
    //   description: 'System reports and activity logs',
    //   items: [
    //     {
    //       icon: FileText,
    //       title: 'System Logs',
    //       description: 'View and export system activity logs',
    //       action: () => alert('System Logs coming soon!'),
    //       color: '#64748b',
    //       comingSoon: true
    //     },
    //     {
    //       icon: AlertCircle,
    //       title: 'Error Logs',
    //       description: 'Monitor and manage system errors',
    //       action: () => alert('Error Logs coming soon!'),
    //       color: '#ef4444',
    //       comingSoon: true
    //     }
    //   ]
    // }
  ];

  return (
      <div className="admin-settings-container">
         <Helmet>
        <title>System Settings | GN VISA Admin</title>
        <meta name="description" content="Configure system settings, manage integrations, and control access permissions for the admin panel." />
      </Helmet>
      <div className="settings-header">
        <div className="header-content">
          <h1 className="settings-title">
            <Settings size={32} />
            System Settings
          </h1>
          <p className="settings-description">
            Configure your system settings, manage integrations, and control access permissions
          </p>
        </div>
      </div>
      <div className="settings-content">
        {settingsCategories.map((category, categoryIndex) => (
          <div key={categoryIndex} className="settings-category">
            <div className="category-header">
              <h2 className="category-title">{category.title}</h2>
              <p className="category-description">{category.description}</p>
            </div>
            <div className="settings-grid">
              {category.items.map((item, itemIndex) => (
                <div 
                  key={itemIndex} 
                  className={`settings-card ${item.comingSoon ? 'coming-soon' : ''}`}
                  onClick={item.action} // This onClick now correctly calls the defined action
                  style={{ '--card-color': item.color }}
                >
                  <div className="card-icon" style={{ backgroundColor: `${item.color}15` }}>
                    <item.icon size={24} style={{ color: item.color }} />
                  </div>
                  <div className="card-content">
                    <h3 className="card-title">
                      {item.title}
                      {item.comingSoon && <span className="coming-soon-badge">Coming Soon</span>}
                    </h3>
                    <p className="card-description">{item.description}</p>
                  </div>
                  <div className="card-arrow">
                    <ChevronRight size={20} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="settings-footer">
        <div className="footer-info">
          <p className="info-text">
            <AlertCircle size={16} />
            Some settings require server restart to take effect
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;